import { CheckIcon, CopyFIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { newLineToBr } from '@prophecy/utils/react/nodes';
import React, { useState } from 'react';
import styled from 'styled-components';

import { Button } from '../Button';
import { Dialog } from '../Dialog';
import { Type as DialogType } from '../Dialog/types';
import { Ellipsis, MultilineEllipsis } from '../Ellipsis';
import { Stack, StackItem } from '../Layout';
import { Text } from '../Typography/Text';
import { StyledButton, ToastCopyContainer, StyledSuccessIcon, DialogCopyContainer } from './styled';
import { toast } from './Toast';
import { Variant as ToastTypes } from './types';

import type { ToastUtilReturnType } from './types';

export enum MessageType {
  info = 'info',
  error = 'error',
  success = 'success',
  warning = 'warning'
}

const StyledText = styled(Text)`
  word-break: break-word;
  position: relative;
`;

const StyledContent = styled(Stack)`
  max-height: calc(70vh - 170px); // 170px is the height of fixed items + gaps in between + padding.
  overflow: auto;
`;

const LinkButton = styled(Button)`
  text-decoration: underline;
  text-underline-offset: ${theme.spaces.x4};
  width: max-content;
  padding: 0;
`;

const DetailContainer = styled(Stack)`
  width: 100%;
  height: 100%;
  position: relative;
`;

const CopyButtonContainer = styled(Stack)`
  position: absolute;
  right: ${theme.spaces.x8};
  top: ${theme.spaces.x4};
  z-index: 1;
`;

type MessageConfig = {
  title?: React.ReactNode;
  content: React.ReactNode;
  detail?: React.ReactNode;
  detailText?: string;
  collapseDetail?: boolean;
  detailButtonText?: React.ReactNode;
  duration?: number; //duration in milliseconds
  copyToClipBoard?: boolean;
  onDetailButtonClick?: () => void;
  width?: number;
};

const MessageTypeDisplayMap = {
  [MessageType.info]: DialogType.info,
  [MessageType.error]: DialogType.alert,
  [MessageType.success]: DialogType.success,
  [MessageType.warning]: DialogType.warning
};

const MessageTypeToastMap = {
  [MessageType.info]: ToastTypes.info,
  [MessageType.error]: ToastTypes.error,
  [MessageType.success]: ToastTypes.success,
  [MessageType.warning]: ToastTypes.error
} as const;

const DEFAULT_DURATION = 10000;

const activeToastMap: Record<MessageType, Map<React.ReactNode, ToastUtilReturnType>> = {
  [MessageType.info]: new Map<React.ReactNode, ToastUtilReturnType>(),
  [MessageType.error]: new Map<React.ReactNode, ToastUtilReturnType>(),
  [MessageType.success]: new Map<React.ReactNode, ToastUtilReturnType>(),
  [MessageType.warning]: new Map<React.ReactNode, ToastUtilReturnType>()
};

function CopyToClipBoard({ content, variant }: { content: React.ReactNode; variant: 'link' | 'linkGray' }) {
  const [showCopied, setShowCopied] = useState(false);
  const onCopyToClipboard = (content: React.ReactNode) => {
    let messageContent = '';

    if (typeof content === 'string') {
      messageContent += content;
    } else if (React.isValidElement(content)) {
      const iterateOverChildren = (children: React.ReactNode | undefined) => {
        if (!children) {
          return;
        }
        React.Children.forEach(children, (child) => {
          if (typeof child === 'string') {
            messageContent += child;
          } else if (React.isValidElement(child)) {
            iterateOverChildren(child.props.children);
          }
        });
      };

      iterateOverChildren(content?.props?.children);
    }

    if (messageContent) {
      navigator.clipboard.writeText(messageContent);
      setShowCopied(true);
      setTimeout(() => {
        setShowCopied(false);
      }, 1000);
    }
  };

  return (
    <>
      {showCopied ? (
        <StyledSuccessIcon
          onClick={() => onCopyToClipboard?.(content)}
          icon={<CheckIcon type='default' size='xs' />}
          variant='link'
          aria-label='Copied'
          data-test-id='copy-success-icon'
        />
      ) : (
        <StyledButton
          tooltip='Copy to Clipboard'
          onClick={() => onCopyToClipboard?.(content)}
          icon={<CopyFIcon type='default' size='xs' />}
          variant={variant}
          aria-label='Copy to clipboard'
          data-test-id='copy-to-clipboard-button'
        />
      )}
    </>
  );
}

export function MessageDetail({ content, detail }: { content: React.ReactNode; detail?: React.ReactNode }) {
  if (!content || (typeof content === 'string' && !content.trim())) return null;
  const _content = newLineToBr(content);

  return (
    <StyledContent gap='1rem'>
      <StyledText level='sm'>{_content}</StyledText>{' '}
      {detail ? (
        <StyledText level='sm'>
          <DialogCopyContainer>
            <CopyToClipBoard content={detail} variant='link' />
          </DialogCopyContainer>
          {newLineToBr(detail)}
        </StyledText>
      ) : null}
    </StyledContent>
  );
}
function ShowMore({ children, defaultShow = false }: { children: React.ReactNode; defaultShow: boolean }) {
  const [show, setShow] = useState(defaultShow);

  return show ? (
    <>{children}</>
  ) : (
    <LinkButton
      size='s'
      variant='link'
      onClick={() => setShow(true)}
      aria-label='Show more details'
      data-test-id='show-more-details-button'>
      More Details
    </LinkButton>
  );
}

const MAX_COLLAPSED_LINES = 10;

function base(messageType: MessageType, config: MessageConfig) {
  const {
    content,
    title = <Text level='sm'>Details</Text>,
    detail,
    detailText,
    onDetailButtonClick,
    duration = DEFAULT_DURATION,
    detailButtonText = 'More..',
    copyToClipBoard = false,
    width: _width,
    collapseDetail = false
  } = config;

  if (!content || (typeof content === 'string' && !content.trim())) return;

  const hasDetailsToShow = detail || (typeof content === 'string' && content.length > 120);

  const showMoreButton = hasDetailsToShow || Boolean(onDetailButtonClick);

  const _content = newLineToBr(content);

  // Clip only when the detail dialog is reachable, so the full message is never lost.
  const canRecoverFullContent = Boolean(hasDetailsToShow) && !onDetailButtonClick;

  const collapsedContent =
    canRecoverFullContent && typeof content === 'string'
      ? newLineToBr(content.split('\n').slice(0, MAX_COLLAPSED_LINES).join('\n'))
      : _content;

  const width = _width || (hasDetailsToShow ? 500 : 400);

  const contentWidth = showMoreButton ? width - 210 : width - 90;

  const showDetailModal = () => {
    const dialogType = MessageTypeDisplayMap[messageType];
    const copyButton = (
      <DialogCopyContainer>
        <CopyToClipBoard content={detailText || detail || content} variant='link' />
      </DialogCopyContainer>
    );
    let detailToRender = null;

    if (detail && typeof detail === 'string') {
      detailToRender = (
        <StyledText level='sm'>
          {copyButton}
          {newLineToBr(detail)}
        </StyledText>
      );
    } else if (detail) {
      detailToRender = (
        <DetailContainer>
          {detail}
          <CopyButtonContainer>{copyButton}</CopyButtonContainer>
        </DetailContainer>
      );
    }

    Dialog[dialogType]({
      children: (
        <StyledContent gap={theme.spaces.x8}>
          <StyledText level='sm'>
            {!detail && copyButton}
            {_content}
          </StyledText>{' '}
          {detailToRender && <ShowMore defaultShow={!collapseDetail}>{detailToRender}</ShowMore>}
        </StyledContent>
      ),
      title,
      size: 'l',
      iconPlacement: 'right',
      closeButton: null,
      'data-test-id': 'toast-detail-dialog'
    });
  };

  const messageContent = (
    <Stack direction='horizontal' gap={theme.spaces.x12} alignY='center' align='space-between' width='100%'>
      <Stack width={contentWidth + 'px'}>
        {showMoreButton ? (
          <Ellipsis tooltip={true} tooltipProps={{ overlayStyle: { width, wordBreak: 'break-all' } }}>
            {collapsedContent}
          </Ellipsis>
        ) : (
          <p>
            <MultilineEllipsis lines={2} expandable={true} symbol='... more' collapsible={true}>
              {_content}
            </MultilineEllipsis>
          </p>
        )}
      </Stack>
      {showMoreButton && (
        <StackItem shrink='0'>
          <Button
            aria-label='Show more details'
            data-test-id='show-more-details-button'
            variant='secondaryGray'
            danger={messageType === MessageType.error}
            size='s'
            onClick={() => {
              if (hasDetailsToShow && !onDetailButtonClick) showDetailModal();
              onDetailButtonClick?.();
            }}>
            {detailButtonText}
          </Button>
        </StackItem>
      )}

      {copyToClipBoard && (
        <StackItem shrink='0'>
          {
            <ToastCopyContainer>
              <CopyToClipBoard content={content} variant='linkGray' />
            </ToastCopyContainer>
          }
        </StackItem>
      )}
    </Stack>
  );

  const toastType = MessageTypeToastMap[messageType];

  if (activeToastMap[messageType].has(content)) {
    const activeToast = activeToastMap[messageType].get(content);
    if (activeToast) {
      activeToast.close();
    }
  }

  const currentToast = toast[toastType]({
    content: messageContent,
    duration,
    width: `${width}`,
    'data-test-id': `toast-${messageType}-toast`,
    onClose: () => {
      activeToastMap[messageType].delete(content);
    }
  });

  activeToastMap[messageType].set(content, currentToast);

  return currentToast;
}

function error(config: MessageConfig) {
  return base(MessageType.error, { copyToClipBoard: true, ...config });
}

function info(config: MessageConfig) {
  return base(MessageType.info, config);
}

function success(config: MessageConfig) {
  return base(MessageType.success, config);
}

function warning(config: MessageConfig) {
  return base(MessageType.warning, config);
}

export const message = { error, info, success, warning };
